import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useColorMode } from '@chakra-ui/react';
import { t } from 'i18next';
import { Button, InputBoxSingle } from '@peacecover/component';
import { ArrowLeft } from '@peacecover/icon';
import { useBettieSettingInfo } from '../../hooks/useBettieSettingInfo';
import { useVirtualKeyboardVisible } from '../hooks/useVirtualKeyboardVisible';
import { updateBettieMoodSelection } from '../../../domains/bettie/services/bettieService';
import { attachJosa, parseMemberCalledMessage } from '../../../utils/bettieUtils';
import PulsingGradientCircle from './PulsingGradientCircle';
import ProfileAvatar from '../../my-page/components/ProfileAvatar';
import { HorizontalSpeechBallon } from '../../../components/drawer/BettieNicknameUpdateBottomDrawer';
import type { BettieSettingStep } from '../BettieSettingPage';
import type { components } from '../../../types/schema';

type ApiV1MemberPersona = components['schemas']['ApiV1MemberPersona'];
type ApiV1BettieMoodSelection = components['schemas']['ApiV1BettieMoodSelection'];

const MOOD_KEYWORDS = {
  personality: [
    t('BettieSetting.moodKeyWords.personality.chicAndWarm'),
    t('BettieSetting.moodKeyWords.personality.calm'),
    t('BettieSetting.moodKeyWords.personality.playful'),
    t('BettieSetting.moodKeyWords.personality.tsundere'),
    t('BettieSetting.moodKeyWords.personality.puppyCharm')
  ],
  speechStyle: [
    t('BettieSetting.moodKeyWords.speechStyle.casual'),
    t('BettieSetting.moodKeyWords.speechStyle.plain'),
    t('BettieSetting.moodKeyWords.speechStyle.chill'),
    t('BettieSetting.moodKeyWords.speechStyle.soft'),
    t('BettieSetting.moodKeyWords.speechStyle.cheerful')
  ],
  romanceStyle: [
    t('BettieSetting.moodKeyWords.romanticStyle.expressive'),
    t('BettieSetting.moodKeyWords.romanticStyle.moderate'),
    t('BettieSetting.moodKeyWords.romanticStyle.subtle'),
    t('BettieSetting.moodKeyWords.romanticStyle.notTooMuch'),
    t('BettieSetting.moodKeyWords.romanticStyle.natural')
  ],
  relationship: [
    t('BettieSetting.moodKeyWords.relationship.justFriends'),
    t('BettieSetting.moodKeyWords.relationship.closeFriends'),
    t('BettieSetting.moodKeyWords.relationship.flirty'),
    t('BettieSetting.moodKeyWords.relationship.complicated'),
    t('BettieSetting.moodKeyWords.relationship.special')
  ]
};

const KEY_ORDER: (keyof ApiV1BettieMoodSelection)[] = [
  'personality',
  'speechStyle',
  'romanceStyle',
  'relationship'
];

interface BettieMoodSuggestionProps {
  setCurrentStep: (step: BettieSettingStep) => void;
  bettiePersona: ApiV1MemberPersona;
  chatRoomUuid: string;
}

const BettieMoodSuggestion = ({
  setCurrentStep,
  bettiePersona,
  chatRoomUuid
}: BettieMoodSuggestionProps) => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const [currentMoodKeyword, setCurrentMoodKeyword] =
    useState<keyof ApiV1BettieMoodSelection>('personality');
  const [moodInput, setMoodInput] = useState({
    relationship: '',
    speechStyle: '',
    personality: '',
    romanceStyle: ''
  });
  const [suggestionMoodKeyword, setSuggestionMoodKeyword] = useState<string>(() => {
    const list = MOOD_KEYWORDS[currentMoodKeyword];
    return list[Math.floor(Math.random() * list.length)];
  });

  const [isTypingDone, setIsTypingDone] = useState(false);
  const prevSuggestionRef = useRef<string | undefined>(undefined);

  const { bettieSettingInfo } = useBettieSettingInfo();
  const { isKeyboardVisible, handleFocus, handleBlur } = useVirtualKeyboardVisible();

  const moodSelectionMessage = bettiePersona?.moodSelectionMessage || [];

  const handleNextClick = async () => {
    const currentIndex = KEY_ORDER.indexOf(currentMoodKeyword);
    if (currentIndex === KEY_ORDER.length - 1) {
      try {
        const requestBody = {
          context: {
            relationship: moodInput.relationship,
            speech_style: moodInput.speechStyle,
            personality: moodInput.personality,
            romance_style: moodInput.romanceStyle
          }
        };

        const response = await updateBettieMoodSelection(chatRoomUuid, requestBody);
        if (response.chatRoom.chatRoomId) {
          setCurrentStep('complete');
        } else {
          throw new Error('Failed to update bettie mood selection');
        }
      } catch (error) {
        console.error(error);
      }
    } else {
      setCurrentMoodKeyword(KEY_ORDER[currentIndex + 1]);
    }
  };

  useEffect(() => {
    setIsTypingDone(false);
    const pickNext = () => {
      const list = MOOD_KEYWORDS[currentMoodKeyword];
      if (!list || list.length === 0) return;
      setSuggestionMoodKeyword((prev) => {
        if (list.length <= 1) {
          return list[0];
        }
        let next = list[Math.floor(Math.random() * list.length)];
        if (next === prev) {
          const idx = (list.indexOf(next) + 1) % list.length;
          next = list[idx];
        }
        return next;
      });
    };
    pickNext();
    const id = setInterval(pickNext, 2500);
    return () => clearInterval(id);
  }, [currentMoodKeyword]);

  useEffect(() => {
    if (
      prevSuggestionRef.current !== undefined &&
      prevSuggestionRef.current !== suggestionMoodKeyword
    ) {
    }
    prevSuggestionRef.current = suggestionMoodKeyword;
  }, [suggestionMoodKeyword]);

  const handleTypingComplete = useCallback(() => {
    setIsTypingDone(true);
  }, []);

  const handlePreviousClick = () => {
    if (KEY_ORDER.indexOf(currentMoodKeyword) === 0) {
      navigate('/bettie-chat-list', { replace: true });
      return;
    }
    setCurrentMoodKeyword(KEY_ORDER[KEY_ORDER.indexOf(currentMoodKeyword) - 1]);
  };

  return (
    <div className="flex flex-col bg-background-light dark:bg-background-dark">
      <div className="fixed top-0 left-0 right-0 z-10 flex justify-between px-[16px] pt-[calc(env(safe-area-inset-top)+10px)] pb-[10px] bg-background-light dark:bg-background-dark">
        <ArrowLeft size={24} className="text-text-main-lv1" onClick={handlePreviousClick} />
        <button
          type="button"
          className="text-[16px] font-medium text-text-main-lv4 leading-[19px]"
          onClick={() => setCurrentStep('complete')}
        >
          {t('BettieSetting.moodSuggestion.skip')}
        </button>
      </div>
      <div className="flex flex-col items-start px-[24px] py-[10px] mt-[44px]">
        <h1 className="text-[26px] font-bold text-text-main-lv1 leading-[31px] whitespace-pre-line">
          {t('BettieSetting.moodSuggestion.title', {
            nickname: attachJosa(bettieSettingInfo.bettieNickname, '이/가')
          })}
        </h1>
      </div>
      <div className="mt-[24px] px-[24px]">
        <div className="flex justify-center gap-[20px] items-center">
          <PulsingGradientCircle
            gradientCircleSize="56px"
            glassCircleSize="56px"
            showDottedCircle={false}
            blur="5.2px"
          >
            <ProfileAvatar
              thumbnailUrl={bettieSettingInfo.dataUrl || ''}
              widthAndHeight="53px"
              svgWidthAndHeight="53px"
            />
          </PulsingGradientCircle>
          <HorizontalSpeechBallon
            enableTyping={true}
            text={parseMemberCalledMessage(moodSelectionMessage?.[currentMoodKeyword], {
              fan_name: bettieSettingInfo.userNickname,
              member_name: bettieSettingInfo.bettieNickname
            })}
            onTypingComplete={handleTypingComplete}
          />
        </div>
      </div>
      <div
        className="flex flex-col items-center gap-[10px] mt-[48px] mx-auto relative"
        style={{
          opacity: isTypingDone ? 1 : 0,
          transition: 'opacity 0.1s ease',
          pointerEvents: isTypingDone ? 'auto' : 'none'
        }}
      >
        <div
          className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-0 w-[166px] h-[48px] pointer-events-none"
          style={{
            background:
              colorMode === 'light'
                ? 'radial-gradient(8854.4% 78.01% at 50% 50%, rgba(203, 32, 255, 0.11) 0%, rgba(203, 32, 255, 0.00) 100%)'
                : 'radial-gradient(8854.4% 78.01% at 50% 50%, rgba(207, 51, 255, 0.18) 0%, rgba(203, 32, 255, 0.00) 100%)',
            filter: 'blur(19px)'
          }}
        />
        <p className="text-[12px] text-text-main-lv4 leading-[14px]">
          {t('BettieSetting.moodSuggestion.mostFrequent')}
        </p>
        <button
          type="button"
          className="text-[15px] font-medium leading-[18px] text-text-primary"
          style={{
            textDecoration: 'underline',
            textUnderlineOffset: '4px',
            textDecorationThickness: '2px'
          }}
          onClick={() => {
            setMoodInput({ ...moodInput, [currentMoodKeyword]: suggestionMoodKeyword });
          }}
        >
          <span key={suggestionMoodKeyword} style={{ animation: 'fadeIn 0.4s ease' }}>
            {suggestionMoodKeyword}
          </span>
        </button>
      </div>
      <div className="px-[24px] mt-[25px] mb-[100px]">
        <InputBoxSingle
          className="bg-transparent"
          placeholder={t('BettieSetting.moodSuggestion.placeholder') as string}
          value={moodInput[currentMoodKeyword]}
          onChange={(e) => setMoodInput({ ...moodInput, [currentMoodKeyword]: e.target.value })}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={50}
        />
      </div>
      <div
        className={`fixed left-0 right-0 ${isKeyboardVisible ? 'bottom-[env(safe-area-inset-bottom)]' : 'px-[24px] bottom-[calc(10px+env(safe-area-inset-bottom))]'}`}
      >
        <div className="mb-[12px] flex items-center justify-center gap-[10px]">
          {Array.from({ length: KEY_ORDER.length }).map((_, index) => (
            <CircleDot
              key={index.toString()}
              fillColor={
                index === KEY_ORDER.indexOf(currentMoodKeyword)
                  ? 'var(--color-text-main-lv3)'
                  : 'var(--color-separator2)'
              }
            />
          ))}
        </div>
        <Button
          size={isKeyboardVisible ? 'fill' : 'large'}
          className="w-full text-[17px] font-semibold"
          onClick={handleNextClick}
          disabled={!moodInput[currentMoodKeyword]}
        >
          {currentMoodKeyword === 'relationship' ? t('done') : t('next')}
        </Button>
      </div>
    </div>
  );
};

export default BettieMoodSuggestion;

const CircleDot = ({ fillColor }: { fillColor: string }) => {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="6"
      height="6"
      viewBox="0 0 6 6"
      fill="none"
      role="img"
      aria-label="circle dot"
    >
      <circle cx="3" cy="3" r="3" fill={fillColor} />
    </svg>
  );
};
